import { useAutoAnimate } from "@formkit/auto-animate/react";
import React from "react";
import { useEffect, useState } from "react";

import { faLinkSimple } from "@performyard/fontawesome/pro-regular-svg-icons";
import { toast } from "@performyard/py-components";
import { Text as Typography } from "@performyard/py-components";
import { ButtonStandard } from "@performyard/py-components";

import { CopyToClipboard } from "../../../../../../components/CopyToClipboard";
import Input from "../../../../../../components/Inputs/InputText";
import { Checkboxes } from "../../../../../../components/Inputs/SelectionOptions/Checkboxes";
import ContentModal from "../../../../../../components/Modal/Templates/ContentModal";
import FormModal from "../../../../../../components/Modal/Templates/FormModal";
import Spacer from "../../../../../../components/Spacer";
import Tooltip from "../../../../../../components/Tooltip";
import { DateWithErrors } from "./DateWithErrors";

import { useSetters as useBuilderSetters } from "../../pages/SurveyBuilder/store/store";
import { useSetters as useSurveyParticipantsSetters } from "../../pages/SurveyParticipants/store/store";

import { SURVEY_DETAILS_MODALS } from "../../store/model";
import {
	useActions,
	useEndDate,
	useModal,
	usePublishFormErrors,
	useSetters,
	useStartDate,
	useSurveyLink,
	useSurveyStatus,
	useValidName,
	useValidQuestions,
} from "../../store/store";

import { useUpdateSurvey } from "../../../../../../api/hooks";
import { useSurvey } from "../../hooks/useSurvey";

import { formatErrorMessage } from "../../../../../../utils/TanstackQueryErrorHandling";

export const PublishSurveyModal = ({ surveyId }: { surveyId: string }) => {
	const { survey } = useSurvey({ surveyId });
	const modal = useModal();
	const startDate = useStartDate();
	const endDate = useEndDate();
	const surveyStatus = useSurveyStatus();
	const setters = useSetters();
	const builderSetters = useBuilderSetters();
	const surveyParticipantsSetters = useSurveyParticipantsSetters();
	const actions = useActions();
	const publishFormErrors = usePublishFormErrors();
	const validQuestions = useValidQuestions();
	const validName = useValidName();
	const [scheduleDates, setScheduleDates] = useState<boolean>(false);

	useEffect(() => {
		if (startDate !== "") {
			setScheduleDates(true);
		}
	}, [startDate]);

	const onSuccess = () => {
		setters.setStartDate("");
		setters.setEndDate("");
		setters.setPublishFormErrors({ startDate: null, endDate: null });
		surveyParticipantsSetters.setSurveyAccess(
			survey?.participantSelection?.enabled ? "selected-employees" : "all-employees"
		);
		setters.setModal(SURVEY_DETAILS_MODALS.PUBLISH_SURVEY_SUCCESS);
	};
	const onError = (error, mutation) => {
		if (error.data?.message?.endsWith("Survey name must be unique")) {
			builderSetters.setInvalidNameMessage("A survey with this name already exists");
		} else {
			//replicate default error handling (turned off for this mutation)
			const messages = formatErrorMessage(error, mutation);

			messages.forEach((message) => {
				toast.error(message);
			});
		}
	};
	const { mutate: publishSurvey } = useUpdateSurvey(surveyId, { onSuccess, onError });

	const handleClickSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (scheduleDates) {
			actions.validateSurveyDates(() => {
				publishSurvey({
					startDate: new Date(startDate),
					endDate: endDate !== "" ? new Date(endDate) : null,
					scheduleDates: true,
					status: "active",
					name: validName,
					questions: validQuestions,
				});
			});
		} else {
			publishSurvey({
				status: "active",
				startDate: new Date(),
				scheduleDates: false,
				name: validName,
				questions: validQuestions,
				endDate: null,
			});
		}
	};

	const handleClose = () => {
		setters.setModal(null);
		setScheduleDates(false);
		setters.setValidQuestions([]);
		setters.setValidName("");
		setters.setStartDate("");
		setters.setEndDate("");
		setters.setPublishFormErrors({ startDate: null, endDate: null });
	};

	const [ref] = useAutoAnimate();

	return (
		<FormModal
			title={
				<div>
					<Typography size="paragraph-1" color="neutral-9" weight="semi-bold">
						Publish this survey?
					</Typography>
					<Typography size="paragraph-2" color="neutral-9" weight="regular">
						Published surveys can start accepting responses
					</Typography>
				</div>
			}
			isOpen={modal === SURVEY_DETAILS_MODALS.PUBLISH_SURVEY}
			handleClose={handleClose}
			buttons={[
				<div key="publish-survey-cancel">
					<ButtonStandard size="large" type="button" onClick={handleClose}>
						Cancel
					</ButtonStandard>
				</div>,
				<div key="publish-survey-submit">
					<ButtonStandard size="large" type="submit" color="green" disabled={surveyStatus !== "draft"}>
						Yes, Publish Survey
					</ButtonStandard>
				</div>,
			]}
			onSubmit={handleClickSubmit}
		>
			<div ref={ref} className="flex flex-col gap-2">
				<Checkboxes
					options={[
						{
							label: "Schedule Start and End Date",
							value: "schedule-dates",
						},
					]}
					value={scheduleDates ? [{ label: "schedule-dates", value: "schedule-dates" }] : []}
					onChange={(options) => {
						const scheduleDates = options.find((option) => option.value === "schedule-dates") !== undefined;
						if (!scheduleDates) {
							setters.setStartDate("");
							setters.setEndDate("");
							setters.setPublishFormErrors({ startDate: null, endDate: null });
						}
						setScheduleDates(scheduleDates);
					}}
				/>
				{scheduleDates ? (
					<>
						<Spacer height={1} />
						<div className="flex flex-col gap-4">
							<DateWithErrors
								value={startDate}
								setValue={(date) => {
									setters.setPublishFormErrors({
										...publishFormErrors,
										startDate: null,
									});
									setters.setStartDate(date ?? "");
								}}
								label="Start Date"
								errorMessage={publishFormErrors["startDate"]}
							/>
							<DateWithErrors
								value={endDate}
								setValue={(date) => {
									setters.setPublishFormErrors({
										...publishFormErrors,
										endDate: null,
									});
									setters.setEndDate(date ?? "");
								}}
								label="End Date"
								errorMessage={publishFormErrors["endDate"]}
							/>
						</div>
					</>
				) : null}
			</div>
		</FormModal>
	);
};

export const PublishSurveyInvalidModal = () => {
	const modal = useModal();
	const setters = useSetters();

	return (
		<ContentModal
			buttons={[
				<div key="publish-survey-invalid-close">
					<ButtonStandard size="large" onClick={() => setters.setModal(null)}>
						Close
					</ButtonStandard>
				</div>,
			]}
			size="small"
			handleClose={() => setters.setModal(null)}
			isOpen={modal === SURVEY_DETAILS_MODALS.PUBLISH_SURVEY_INVALID}
		>
			<div className="flex w-full flex-col items-center gap-2">
				<Typography size="paragraph-1" color="neutral-9" weight="semi-bold">
					This survey cannot be published.
				</Typography>
				<Typography size="paragraph-2" color="neutral-9" weight="regular">
					<div className="text-center">
						At least one answerable question is required for a survey to be published
					</div>
				</Typography>
			</div>
		</ContentModal>
	);
};

export const PublishSurveySuccessModal = () => {
	const modal = useModal();
	const setters = useSetters();
	const surveyLink = useSurveyLink();

	return (
		<ContentModal
			isOpen={modal === SURVEY_DETAILS_MODALS.PUBLISH_SURVEY_SUCCESS}
			handleClose={() => setters.setModal(null)}
		>
			<div className="flex w-full flex-col items-center gap-4">
				<Typography size="paragraph-1" color="neutral-9" weight="semi-bold">
					Your Survey Has Been Published!
				</Typography>
				<div className="w-full text-center">
					<Input value={surveyLink} />
				</div>
				<Tooltip
					key="publish-survey-success-copy-link"
					message="Survey Link Copied"
					openOnClick
					hasArrow={false}
					turnOffAfter={2000}
					placement="left"
				>
					<CopyToClipboard text={surveyLink}>
						<div className="flex h-12 items-center">
							<ButtonStandard size="large" iconLeft={faLinkSimple}>
								Copy Link
							</ButtonStandard>
						</div>
					</CopyToClipboard>
				</Tooltip>
			</div>
		</ContentModal>
	);
};
